import React, { useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { MoreHorizontal, Eye, Edit, Trash2, Key, User, AlertCircle } from 'lucide-react'
import PaginationComponent from '@/components/common/PaginationComponent'
import DeleteTripDialog from './deletetrip/DeleteTripDialog'
import NicSyncDialog from './NicSyncDialog'
import { formatTableText } from '@/lib/tableUtils'
import { useAuth } from '@/contexts/AuthContext'

const TripTable = ({
  trips,
  onGenerateStartOtp,
  onViewDetails,
  onViewOtpDetails,
  onEditTrip,
  onDeleteTrip,
  onChangeDriver,
  onCreateTicket,
  currentPage,
  totalPages,
  totalTrips,
  pageSize,
  onPageChange,
  isDistributor,
  isTransporter,
  isSuperAdmin: isSuperAdminProp
}) => {
  const { userRole, userProfile } = useAuth();
  const role = (userProfile?.role || userProfile?.user_role || userRole || '').toLowerCase();
  const isSuperAdmin = isSuperAdminProp !== undefined
    ? isSuperAdminProp
    : ['ipl_super_admin', 'super_admin', 'superadmin'].includes(role);

  const [tripToDelete, setTripToDelete] = useState(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [selectedSyncTrip, setSelectedSyncTrip] = useState(null)
  const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false)

  const handleNicSyncClick = (trip) => {
    setSelectedSyncTrip(trip)
    setIsSyncDialogOpen(true)
  }

  const getStatusVariant = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'active'
      case 'COMPLETED':
      case 'AUTO_COMPLETE':
      case 'AUTO_COMPLETED':
        return 'completed'
      case 'DELAYED':
        return 'delayed'
      case 'SCHEDULED':
        return 'scheduled'
      case 'PENDING':
        return 'scheduled'
      default:
        return 'default'
    }
  }

  const formatStatus = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'Active'
      case 'COMPLETED':
        return 'Completed'
      case 'AUTO_COMPLETE':
      case 'AUTO_COMPLETED':
        return 'Auto Completed'
      case 'DELAYED':
        return 'Delayed'
      case 'SCHEDULED':
        return 'Scheduled'
      case 'PENDING':
        return 'Pending'
      default:
        return status
    }
  }

  const canEditTrip = (status) => {
    return status === 'PENDING';
  }

  const canDeleteTrip = (status) => {
    return status === 'PENDING';
  }

  const canChangeDriver = (status) => {
    return status === 'ACTIVE' || status === 'PENDING';
  }

  const canRaiseTicket = (status) => {
    return status === 'ACTIVE' || status === 'COMPLETED' || status === 'AUTO_COMPLETE' || status === 'AUTO_COMPLETED' || status === 'CANCELLED';
  }

  const handleDeleteTrip = (trip) => {
    setTripToDelete(trip)
    setIsDeleteDialogOpen(true)
  }

  const confirmDeleteTrip = async (trip) => {
    setIsDeleting(true)
    try {
      await onDeleteTrip(trip.id)
    } finally {
      setIsDeleting(false)
      setIsDeleteDialogOpen(false)
      setTripToDelete(null)
    }
  }

  return (
    <>
      <div className="rounded-md border overflow-hidden w-full">
        <div className="max-h-[calc(100vh-250px)] overflow-auto w-full">
          <Table>
            <TableHeader className="sticky top-0 z-10">
              <TableRow>
                <TableHead className="h-10 px-4 py-1 min-w-[120px]">Trip Number</TableHead>
                <TableHead className="h-10 px-4 py-1 min-w-[150px]">Driver</TableHead>
                <TableHead className="h-10 px-4 py-1 min-w-[130px]">Port</TableHead>
                <TableHead className="h-10 px-4 py-1 min-w-[110px]">Truck</TableHead>
                <TableHead className="h-10 px-4 py-1 min-w-[160px]">Route</TableHead>
                <TableHead className="h-10 px-4 py-1 min-w-[130px]">Product</TableHead>
                <TableHead className="h-10 px-4 py-1 min-w-[100px]">Status</TableHead>
                <TableHead className="h-10 px-4 py-1 min-w-[130px]">ETA</TableHead>
                <TableHead className="h-10 px-4 py-1 min-w-[140px]">Created At</TableHead>
                {isSuperAdmin && (
                  <TableHead className="h-10 px-4 py-1 min-w-[140px]">NIC Trip Sync</TableHead>
                )}
                <TableHead className="h-10 px-4 py-1 text-right min-w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trips.map((trip) => {
                const hasSap = Boolean(
                  trip.hasSapDispatch ??
                  trip.has_sap_dispatch ??
                  trip.rawTrip?.has_sap_dispatch ??
                  (Array.isArray(trip.items) && trip.items.some((i) => i?.has_sap_dispatch)) ??
                  (Array.isArray(trip.rawTrip?.items) && trip.rawTrip.items.some((i) => i?.has_sap_dispatch))
                );

                return (
                  <TableRow
                    key={trip.id}
                    className={`border-border transition-colors ${
                      hasSap
                        ? 'bg-emerald-500/[0.08] dark:bg-emerald-950/30 hover:bg-emerald-500/[0.14] dark:hover:bg-emerald-950/45 border-l-4 border-l-emerald-600'
                        : 'hover:bg-muted/40'
                    }`}
                  >
                    <TableCell className="h-10 px-4 py-1 font-medium">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span>{formatTableText(trip.tripNumber) || 'N/A'}</span>
                        {hasSap ? (
                          <Badge
                            variant="outline"
                            className="h-5 px-1.5 py-0 text-[10px] font-semibold bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30 whitespace-nowrap inline-flex items-center gap-1"
                            title="SAP Dispatched"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                            SAP
                          </Badge>
                        ) : isSuperAdmin ? (
                          <Badge
                            variant="outline"
                            className="h-5 px-1.5 py-0 text-[10px] font-medium bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30 whitespace-nowrap inline-flex items-center gap-1"
                            title="SAP Pending"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                            SAP Pending
                          </Badge>
                        ) : null}
                      </div>
                    </TableCell>
                  <TableCell className="h-10 px-4 py-1">
                    <div>{formatTableText(trip.driver) || 'N/A'}</div>
                    {trip.driverPhone && <div className="text-xs text-muted-foreground">{trip.driverPhone}</div>}
                  </TableCell>
                  <TableCell className="h-10 px-4 py-1">{formatTableText(trip.port) || 'N/A'}</TableCell>
                  <TableCell className="h-10 px-4 py-1 uppercase">{formatTableText(trip.truck) || 'N/A'}</TableCell>
                  <TableCell className="h-10 px-4 py-1">{formatTableText(trip.route) || 'N/A'}</TableCell>
                  <TableCell className="h-10 px-4 py-1">{formatTableText(trip.product) || 'N/A'}</TableCell>
                  <TableCell className="h-10 px-4 py-1">
                    <Badge
                      variant={getStatusVariant(trip.status)}
                      className="inline-block text-center whitespace-nowrap"
                    >
                      {formatStatus(trip.status) || 'N/A'}
                    </Badge>
                  </TableCell>
                  <TableCell className="h-10 px-4 py-1">{formatTableText(trip.eta) || 'N/A'}</TableCell>
                  <TableCell className="h-10 px-4 py-1">{formatTableText(trip.createdAt) || 'N/A'}</TableCell>
                  {/* <TableCell className="h-10 px-4 py-1 text-muted-foreground">--</TableCell>
                  <TableCell className="h-10 px-4 py-1 text-muted-foreground">--</TableCell>
                  <TableCell className="h-10 px-4 py-1 text-muted-foreground">--</TableCell> */}
                  {isSuperAdmin && (
                    <TableCell className="h-10 px-4 py-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 px-2.5 text-xs bg-primary/10 text-primary hover:bg-primary/20 border-primary/30 font-medium inline-flex items-center gap-1.5 transition-all shadow-xs"
                        onClick={() => handleNicSyncClick(trip)}
                        title="View NIC Trip Sync Logs"
                      >
                        <span>NIC Trip Sync</span>
                        <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold leading-none">
                          !
                        </span>
                      </Button>
                    </TableCell>
                  )}
                  <TableCell className="h-10 px-4 py-1 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onViewDetails(trip.id)}>
                          <Eye className="mr-2 h-4 w-4" />
                          View Details
                        </DropdownMenuItem>

                        {onCreateTicket && canRaiseTicket(trip.status) && (
                          <DropdownMenuItem onClick={() => onCreateTicket(trip)}>
                            <AlertCircle className="mr-2 h-4 w-4 text-amber-500" />
                            Request Correction
                          </DropdownMenuItem>
                        )}

                        {isTransporter && onChangeDriver && canChangeDriver(trip.status) && (
                          <DropdownMenuItem onClick={() => onChangeDriver(trip)}>
                            <User className="mr-2 h-4 w-4" />
                            Change Driver
                          </DropdownMenuItem>
                        )}

                        {isTransporter && onChangeDriver && !canChangeDriver(trip.status) && (
                          <DropdownMenuItem disabled>
                            <User className="mr-2 h-4 w-4" />
                            Change Driver
                          </DropdownMenuItem>
                        )}

                        {isTransporter && (
                          <DropdownMenuItem onClick={() => onViewOtpDetails(trip.id)}>
                            <Key className="mr-2 h-4 w-4" />
                            Show OTPs
                          </DropdownMenuItem>
                        )}

                        {onEditTrip && canEditTrip(trip.status) && (
                          <DropdownMenuItem onClick={() => onEditTrip(trip.id)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit Trip
                          </DropdownMenuItem>
                        )}

                        {onEditTrip && !canEditTrip(trip.status) && (
                          <DropdownMenuItem disabled>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit Trip
                          </DropdownMenuItem>
                        )}

                        {onDeleteTrip && canDeleteTrip(trip.status) && (
                          <DropdownMenuItem
                            onClick={() => handleDeleteTrip(trip)}
                            className="text-red-500"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete Trip
                          </DropdownMenuItem>
                        )}

                        {onDeleteTrip && !canDeleteTrip(trip.status) && (
                          <DropdownMenuItem disabled className="text-red-500">
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete Trip
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
            </TableBody>
          </Table>
        </div>

        <PaginationComponent
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalTrips}
          pageSize={pageSize}
          onPageChange={onPageChange}
          className="border-t"
        />
      </div>

      <DeleteTripDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        trip={tripToDelete}
        onDeleteTrip={confirmDeleteTrip}
        isDeleting={isDeleting}
      />

      {isSuperAdmin && (
        <NicSyncDialog
          open={isSyncDialogOpen}
          onOpenChange={setIsSyncDialogOpen}
          trip={selectedSyncTrip}
        />
      )}
    </>
  )
}

export default TripTable